import { api } from '../api.js';
import { esc, dateTime, toast, guard } from '../ui.js';

export async function emailsView(root, me) {
  const canEdit = me.groups.some((g) => g === 'Owner' || g === 'Manager');
  const { templates, tags } = await api('/email-templates');
  let active = templates[0]?.key;
  const drafts = Object.fromEntries(templates.map((t) => [t.key, { ...t }]));

  root.innerHTML = `
    <div class="page-head"><div style="display:flex;flex-direction:column;gap:6px">
      <h1>Automated emails</h1>
      <p class="muted">Edit what guests receive automatically. Changes go live when you publish.</p></div></div>
    <div class="tabs" id="tabs" role="group" aria-label="Email"></div>
    <div class="split">
      <form class="box" id="editor" novalidate></form>
      <div style="display:flex;flex-direction:column;gap:16px">
        <div class="page-head"><span class="field-label">Preview (sample guest: Jordan, party of 2)</span>
          <button class="btn small" type="button" id="refresh">Refresh preview</button></div>
        <iframe class="preview" id="preview" title="Email preview" sandbox=""></iframe>
        <div class="box" id="versions"><h2>Version history</h2></div>
      </div>
    </div>`;

  const tabs = () => {
    root.querySelector('#tabs').innerHTML = templates.map((t) => `<button type="button" class="chip" data-key="${t.key}" aria-pressed="${t.key === active}">${esc(t.label)}</button>`).join('');
  };

  const editor = () => {
    const t = drafts[active];
    const dis = canEdit ? '' : 'disabled';
    root.querySelector('#editor').innerHTML = `
      <div class="summary-row"><span>Sends ${esc(t.trigger_desc)}</span>
        <label class="check"><input type="checkbox" name="enabled" ${t.enabled ? 'checked' : ''} ${dis}><span>Automation on</span></label></div>
      <label class="field"><span>Subject line</span><input name="subject" value="${esc(t.subject)}" ${dis}></label>
      <label class="field"><span>Message</span><textarea name="body" rows="14" ${dis}>${esc(t.body)}</textarea></label>
      ${canEdit ? `<div style="display:flex;flex-direction:column;gap:10px"><span class="field-label">Insert guest details</span>
        <div class="tag-buttons">${tags.map((g) => `<button type="button" data-tag="${g}">{${g}}</button>`).join('')}</div></div>` : ''}
      <label class="check"><input type="checkbox" name="includeVideo" ${t.include_video ? 'checked' : ''} ${dis}><span>Include the welcome video link</span></label>
      <label class="check"><input type="checkbox" name="includePayButton" ${t.include_pay_button ? 'checked' : ''} ${dis}><span>Include a payment button</span></label>
      ${canEdit ? `<div class="toolbar">
        <button class="btn small" type="button" id="test">Send test to me</button>
        <button class="btn small" type="button" id="revert">Discard changes</button>
        <button class="btn small primary" type="submit">Publish changes</button></div>
        <p class="small muted">Only Owners and Managers can publish. Every published version is saved and can be restored.</p>`
      : '<p class="notice">You can view these emails. Owners and Managers can edit them.</p>'}`;
  };

  const readForm = () => {
    const f = new FormData(root.querySelector('#editor'));
    const t = drafts[active];
    if (!canEdit) return t;
    Object.assign(t, {
      subject: f.get('subject'), body: f.get('body'), enabled: f.get('enabled') === 'on',
      include_video: f.get('includeVideo') === 'on', include_pay_button: f.get('includePayButton') === 'on',
    });
    return t;
  };
  const payload = (t) => ({ subject: t.subject, body: t.body, enabled: t.enabled, includeVideo: t.include_video, includePayButton: t.include_pay_button });

  const preview = guard(async () => {
    const r = await api(`/email-templates/${active}/preview`, { method: 'POST', body: payload(readForm()) });
    root.querySelector('#preview').srcdoc = r.html.replace('<body', `<body><p style="font-family:Arial;color:#cfc0a9;font-size:13px;padding:10px 16px;margin:0;background:#111">Subject: ${esc(r.subject)}</p><div`).replace(/<\/body>/, '</div></body>');
  });

  const versions = guard(async () => {
    const { versions: vs } = await api(`/email-templates/${active}/versions`);
    const current = templates.find((t) => t.key === active).version;
    root.querySelector('#versions').innerHTML = `<h2>Version history</h2>${vs.map((v) => `<div class="summary-row small">
      <span>v${v.version}${v.version === current ? ' · <strong>Live</strong>' : ''} · ${esc(v.published_by || 'Default')}</span>
      <span>${esc(dateTime(v.published_at))}${canEdit && v.version !== current ? ` · <a href="#" data-restore="${v.version}">Restore</a>` : ''}</span></div>`).join('')}`;
  });

  const show = () => { tabs(); editor(); preview(); versions(); };

  root.querySelector('#tabs').addEventListener('click', (e) => {
    const b = e.target.closest('[data-key]');
    if (!b) return;
    readForm(); active = b.dataset.key; show();
  });
  root.querySelector('#refresh').addEventListener('click', preview);
  root.querySelector('#editor').addEventListener('click', guard(async (e) => {
    const tag = e.target.closest('[data-tag]');
    if (tag) {
      const ta = root.querySelector('textarea[name=body]');
      const ins = `{${tag.dataset.tag}}`;
      const s = ta.selectionStart ?? ta.value.length;
      ta.value = ta.value.slice(0, s) + ins + ta.value.slice(ta.selectionEnd ?? s);
      ta.focus(); ta.selectionStart = ta.selectionEnd = s + ins.length;
      return;
    }
    if (e.target.closest('#test')) {
      const r = await api(`/email-templates/${active}/test`, { method: 'POST', body: payload(readForm()) });
      toast(`Test sent to ${r.sentTo}`);
    }
    if (e.target.closest('#revert')) {
      drafts[active] = { ...templates.find((t) => t.key === active) };
      editor(); preview();
    }
  }));
  root.querySelector('#editor').addEventListener('submit', guard(async (e) => {
    e.preventDefault();
    const saved = await api(`/email-templates/${active}`, { method: 'PUT', body: payload(readForm()) });
    const i = templates.findIndex((t) => t.key === active);
    templates[i] = saved; drafts[active] = { ...saved };
    toast(`Published version ${saved.version}`);
    show();
  }));
  root.querySelector('#versions').addEventListener('click', guard(async (e) => {
    const a = e.target.closest('[data-restore]');
    if (!a) return;
    e.preventDefault();
    if (!confirm(`Restore version ${a.dataset.restore}? It becomes the live email.`)) return;
    const saved = await api(`/email-templates/${active}/restore/${a.dataset.restore}`, { method: 'POST' });
    const i = templates.findIndex((t) => t.key === active);
    templates[i] = saved; drafts[active] = { ...saved };
    toast(`Restored as version ${saved.version}`);
    show();
  }));

  show();
}
